import type {
  ClubhouseAdKitImageDto,
  ClubhouseAdKitImagesUploadResponse,
} from "entities/responses/adkitImage";
import {
  formatAspectRatioLabel,
  type PlacementCatalogEntry,
} from "../../placementCatalog";
import { pluralize } from "../../pluralize";
import { imageFitsPlacement } from "./imageLibraryViewModel";

export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;
export const ACCEPTED_UPLOAD_TYPES = ["image/png", "image/jpeg"];
export const ACCEPTED_UPLOAD_EXTENSIONS = ".png,.jpg,.jpeg";

export type UploadOutcome =
  | { kind: "uploaded"; image: ClubhouseAdKitImageDto }
  /** Didn't match a spot. `image` is set if the API kept it anyway. */
  | {
      kind: "size-warning";
      image?: ClubhouseAdKitImageDto;
      width?: number;
      height?: number;
    }
  | { kind: "duplicate" }
  | { kind: "too-large" }
  | { kind: "wrong-type" }
  | { kind: "failed" };

/** Caught before sending, so the operator isn't made to wait for a 413. */
export function uploadRejection(file: {
  type: string;
  size: number;
}): UploadOutcome | null {
  if (!ACCEPTED_UPLOAD_TYPES.includes(file.type)) return { kind: "wrong-type" };
  if (file.size > MAX_UPLOAD_BYTES) return { kind: "too-large" };
  return null;
}

export type UploadResult =
  /** 201/207: already parsed by the endpoint's transformResponse. */
  | { ok: true; body: ClubhouseAdKitImagesUploadResponse }
  /** Any other status, with the raw error body if there was one. */
  | { ok: false; status: number | string | undefined; body: unknown };

/** Turns one file's upload result (201, 207, 409, 413, …) into a row state. */
export function classifyUploadResult(result: UploadResult): UploadOutcome {
  if (!result.ok) {
    if (result.status === 409) return { kind: "duplicate" };
    if (result.status === 413) return { kind: "too-large" };
    return { kind: "failed" };
  }
  const uploaded = result.body.uploadedImages?.[0];
  const warning = result.body.errors?.[0];
  if (warning) {
    const dims = warning.request?.dimensions ?? uploaded?.dimensions;
    return {
      kind: "size-warning",
      image: uploaded ?? undefined,
      width: dims?.width,
      height: dims?.height,
    };
  }
  return uploaded ? { kind: "uploaded", image: uploaded } : { kind: "failed" };
}

/**
 * The API accepts mismatched shapes without a warning (checked live: a
 * 1500×900 PNG came back 201), so an uploaded image that fits no spot is
 * turned into a size warning here, letting the operator crop or remove it.
 */
export function flagMismatchedShape(
  outcome: UploadOutcome,
  placements: PlacementCatalogEntry[],
): UploadOutcome {
  if (outcome.kind !== "uploaded" || placements.length === 0) return outcome;
  const fitsSomewhere = placements.some((p) =>
    imageFitsPlacement(outcome.image, p),
  );
  if (fitsSomewhere || !outcome.image.dimensions) return outcome;
  return {
    kind: "size-warning",
    image: outcome.image,
    width: outcome.image.dimensions.width,
    height: outcome.image.dimensions.height,
  };
}

/** "fits 6 spots" / "doesn’t fit a spot exactly" */
export function describeFit(
  image: ClubhouseAdKitImageDto,
  placements: PlacementCatalogEntry[],
): string {
  const count = placements.filter((p) => imageFitsPlacement(image, p)).length;
  return count > 0
    ? `fits ${pluralize(count, "spot")}`
    : "doesn’t fit a spot exactly";
}

export interface CropShape {
  /** "2:1" */
  label: string;
  aspectRatio: number;
  /** Short form for the cropper's "for …" text: "3D Tee Sign, Hole Load and 5 more". */
  name: string;
  /** Every placement of this shape, for the shape chooser. */
  placementNames: string;
  /** The largest size any placement of this shape uses, so one crop serves them all. */
  width?: number;
  height?: number;
}

/** One entry per distinct placement shape, widest ratio last. */
export function buildCropShapes(
  placements: PlacementCatalogEntry[],
): CropShape[] {
  const byLabel = new Map<string, PlacementCatalogEntry[]>();
  for (const p of placements) {
    if (!p.aspectRatio) continue;
    const label = formatAspectRatioLabel(p.aspectRatio);
    byLabel.set(label, [...(byLabel.get(label) ?? []), p]);
  }
  return [...byLabel.entries()]
    .map(([label, group]) => {
      const largest = group.reduce((a, b) =>
        (b.width ?? 0) > (a.width ?? 0) ? b : a,
      );
      return {
        label,
        aspectRatio: largest.aspectRatio,
        name:
          group.length > 2
            ? `${group
                .slice(0, 2)
                .map((p) => p.name)
                .join(", ")} and ${group.length - 2} more`
            : group.map((p) => p.name).join(" and "),
        placementNames: group.map((p) => p.name).join(", "),
        width: largest.width,
        height: largest.height,
      };
    })
    .sort((a, b) => a.aspectRatio - b.aspectRatio);
}
